'use client';

import { use, useMemo, useState } from 'react';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    Deposit,
    validUnits
} from '@/lib/data';
import {
    Building2,
    Search,
    ChevronDown,
    ChevronRight,
    HandCoins,
    Info,
    X,
    Filter,
    ArrowUpCircle,
    Calendar,
    Wallet,
    CheckCircle2,
    Clock,
    Eye
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';

export default function DepositPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const baseType = initialUnit.split('-')[0];
    const sisterUnits = validUnits.filter(u =>
        u.startsWith(baseType) &&
        (u.includes('-') || !validUnits.some(v => v.startsWith(baseType) && v.includes('-')))
    );

    const defaultUnit = initialUnit.includes('-')
        ? initialUnit as SchoolUnit
        : (sisterUnits.find(u => u.includes('-')) || initialUnit) as SchoolUnit;

    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(defaultUnit);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [selectedDeposit, setSelectedDeposit] = useState<Deposit | null>(null);

    const schoolData = getSchoolData(activeUnit);
    const schoolName = getSchoolName(activeUnit);

    const camelCaseUnit = activeUnit.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const deposits = (
        (schoolData as any)[`${camelCaseUnit}Deposits`] ||
        (schoolData as any)[`${activeUnit.split('-')[0]}Deposits`] ||
        []
    ) as Deposit[];

    const filteredDeposits = useMemo(() => {
        return deposits.filter(d => {
            const matchesSearch = (d.source || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (d.description || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'All' || d.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [deposits, searchTerm, statusFilter]);

    const stats = useMemo(() => {
        return {
            total: 'Rp 25.700.000',
            count: deposits.length,
            pending: deposits.filter(d => d.status === 'pending').length
        };
    }, [deposits]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href={`/yayasan/${initialUnit}/keuangan`} className="hover:text-primary transition-all">Keuangan</Link>
                                <ChevronRight className="w-3.5 h-3.5" />
                                <span className="text-foreground">Setoran</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <HandCoins className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Setoran Kas</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-primary" /> Pantau Dana Masuk Unit {baseType.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Unit Selector */}
                        <div className="relative min-w-[280px]">
                            <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block ml-1">Pilih Unit Sekolah</label>
                            <div className="relative group">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                                <select
                                    className="w-full pl-11 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all appearance-none cursor-pointer outline-none shadow-sm"
                                    value={activeUnit}
                                    onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                                >
                                    {sisterUnits.map(u => (
                                        <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Cards */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 bg-primary-soft text-primary rounded-xl flex items-center justify-center">
                            <Wallet className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] font-bold text-success bg-success-soft px-2 py-1 rounded-lg">+12% vs last month</span>
                    </div>
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Setoran Periode Ini</p>
                    <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">{stats.total}</p>
                </div>
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-info-soft text-info rounded-2xl flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Setoran Berhasil</p>
                        <p className="text-xl font-bold text-foreground">{stats.count} Transaksi</p>
                    </div>
                </div>
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-warning-soft text-warning rounded-2xl flex items-center justify-center">
                        <Clock className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Menunggu Verifikasi</p>
                        <p className="text-xl font-bold text-foreground">{stats.pending} Transaksi</p>
                    </div>
                </div>
            </section>

            {/* Deposits Table */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col min-h-[400px]">
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari sumber dana atau keterangan..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="bg-background border border-border-main rounded-2xl px-4 py-2.5 flex items-center gap-2">
                            <Filter className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent text-[10px] font-bold uppercase tracking-widest outline-none cursor-pointer"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="All">Semua Status</option>
                                <option value="completed">Berhasil</option>
                                <option value="pending">Tertunda</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Sumber Dana</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Metode</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Nominal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main font-medium">
                            {filteredDeposits.length > 0 ? filteredDeposits.map((deposit) => (
                                <tr key={deposit.id} className="hover:bg-primary-soft/10 transition-colors group text-sm">
                                    <td className="px-8 py-5 whitespace-nowrap text-secondary flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5 opacity-50" />
                                        {deposit.date}
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <p className="font-bold text-foreground">{deposit.source}</p>
                                        <p className="text-[10px] text-muted truncate max-w-[200px] mt-0.5 uppercase tracking-widest">{deposit.description}</p>
                                    </td>
                                    <td className="px-8 py-5 text-center whitespace-nowrap">
                                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">{deposit.paymentMethod}</span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap font-bold text-foreground tabular-nums">
                                        {deposit.amount}
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${deposit.status === 'completed' ? 'bg-success-soft text-success' :
                                            deposit.status === 'pending' ? 'bg-warning-soft text-warning' : 'bg-error-soft text-error'
                                            }`}>
                                            {deposit.status === 'completed' ? 'Berhasil' :
                                                deposit.status === 'pending' ? 'Tertunda' : 'Gagal'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <button
                                            onClick={() => setSelectedDeposit(deposit)}
                                            className="p-2 rounded-xl bg-background border border-border-main text-muted hover:text-primary hover:border-primary transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-32 text-center text-muted">
                                        <div className="flex flex-col items-center gap-4 opacity-30">
                                            <HandCoins className="w-16 h-16" />
                                            <p className="font-bold text-sm uppercase tracking-widest">Tidak ada setoran ditemukan</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Detail Modal */}
            {selectedDeposit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setSelectedDeposit(null)}></div>
                    <div className="relative bg-surface w-full max-w-lg rounded-4xl border border-border-main shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-background/50">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary">
                                    <HandCoins className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">Detail Setoran</h3>
                                    <p className="text-secondary text-xs font-medium uppercase tracking-widest mt-0.5">ID: {selectedDeposit.id}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedDeposit(null)} className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-foreground hover:rotate-90 transition-all duration-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal Setoran</p>
                                    <p className="text-sm font-bold text-foreground">{selectedDeposit.date}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Metode Pembayaran</p>
                                    <p className="text-sm font-bold text-foreground">{selectedDeposit.paymentMethod}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Sumber Dana</p>
                                    <p className="text-sm font-bold text-foreground">{selectedDeposit.source}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Rekening Tujuan</p>
                                    <p className="text-sm font-bold text-primary">BCA - Operasional</p>
                                </div>
                            </div>

                            <div className="p-6 bg-background rounded-3xl border border-border-main">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Nominal Setoran</p>
                                <p className="text-3xl font-bold text-foreground tabular-nums tracking-tight">{selectedDeposit.amount}</p>
                            </div>

                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan Tambahan</p>
                                <div className="p-4 bg-background border border-border-main rounded-2xl text-xs font-medium text-secondary leading-relaxed">
                                    {selectedDeposit.description}
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex justify-end">
                            <button onClick={() => setSelectedDeposit(null)} className="px-8 py-3.5 bg-surface border border-border-main text-secondary rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-background transition-all">Tutup</button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
